import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getResponseSummary, getSegmentedResponses, getMembershipSummary } from '../services/reportService';
import { getProjects } from '../services/projectService';
import { getReports, generateReport } from '../services/generatedReportService';

const STATUS_LABELS = {
  anonymous: 'Anonymous (Public)',
  unverified: 'Unverified',
  self_declared: 'Self-Declared',
  community_pending: 'Community Pending',
  community_verified: 'Community Verified',
  revoked: 'Revoked',
};

const ReportsPage = () => {
  const { activeTenant } = useAuth();
  const tenantId = activeTenant?._id;
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [responseSummary, setResponseSummary] = useState(null);
  const [segmented, setSegmented] = useState(null);
  const [membershipSummary, setMembershipSummary] = useState(null);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [genMsg, setGenMsg] = useState('');
  const [genForm, setGenForm] = useState({ title: '', type: 'public_summary', recommendations: '' });

  useEffect(() => {
    if (tenantId) {
      getProjects(tenantId)
        .then((res) => setProjects(res.data))
        .catch(console.error);

      getMembershipSummary({ tenantId })
        .then((res) => setMembershipSummary(res.data))
        .catch(console.error);

      getReports({ tenantId })
        .then((res) => setGeneratedReports(res.data))
        .catch(console.error);
    }
  }, [tenantId]);

  const handleLoadReport = async () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const [summaryRes, segmentedRes] = await Promise.all([
        getResponseSummary({ tenantId, projectId: selectedProject }),
        getSegmentedResponses({ tenantId, projectId: selectedProject }),
      ]);
      setResponseSummary(summaryRes.data);
      setSegmented(segmentedRes.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!selectedProject) { setGenMsg('Select a project first.'); return; }
    setGenMsg('');
    setGenerating(true);
    try {
      const res = await generateReport({
        tenantId,
        projectId: selectedProject,
        title: genForm.title,
        type: genForm.type,
        recommendations: genForm.recommendations
          ? genForm.recommendations.split('\n').map((r) => r.trim()).filter(Boolean)
          : [],
      });
      setGeneratedReports((prev) => [res.data, ...prev]);
      setGenForm({ title: '', type: 'public_summary', recommendations: '' });
      setGenMsg('Report generated. View it below.');
    } catch (err) {
      setGenMsg(err.response?.data?.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="page reports-page">
      <h2>Reports</h2>

        {/* Membership Summary */}
        {membershipSummary && (
          <section className="report-section">
            <h3>Membership Overview</h3>
            <p>Total Claims: <strong>{membershipSummary.totalClaims}</strong></p>
            <table className="report-table">
              <thead><tr><th>Status</th><th>Count</th></tr></thead>
              <tbody>
                {membershipSummary.byStatus.map((row) => (
                  <tr key={row._id}>
                    <td>{STATUS_LABELS[row._id] || row._id}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Project Response Report */}
        <section className="report-section">
          <h3>Response Report by Project</h3>
          <div className="report-controls">
            <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
              <option value="">-- Select Project --</option>
              {projects.map((p) => <option key={p._id} value={p._id}>{p.title}</option>)}
            </select>
            <button onClick={handleLoadReport} disabled={loading}>
              {loading ? 'Loading...' : 'Load Report'}
            </button>
          </div>

          {error && <div className="error-msg">{error}</div>}

          {responseSummary && (
            <div className="report-results">
              <p>Total Responses: <strong>{responseSummary.totalResponses}</strong></p>

              <h4>By Membership Status</h4>
              <table className="report-table">
                <thead><tr><th>Segment</th><th>Count</th></tr></thead>
                <tbody>
                  {responseSummary.byMembershipStatus.map((row) => (
                    <tr key={row._id}>
                      <td>{STATUS_LABELS[row._id] || row._id}</td>
                      <td>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4>By Response Scope</h4>
              <table className="report-table">
                <thead><tr><th>Scope</th><th>Count</th></tr></thead>
                <tbody>
                  {responseSummary.byResponseScope.map((row) => (
                    <tr key={row._id}>
                      <td>{row._id}</td>
                      <td>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {segmented && segmented.segments.length > 0 && (
            <div className="report-segments">
              <h4>Segmented Response Breakdown</h4>
              <div className="segment-bars">
                {segmented.segments.map((s) => {
                  const total = segmented.segments.reduce((acc, x) => acc + x.count, 0);
                  const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
                  return (
                    <div key={s.segment} className="segment-bar-row">
                      <span className="segment-label">{STATUS_LABELS[s.segment] || s.segment}</span>
                      <div className="segment-bar-track">
                        <div className={`segment-bar fill-${s.segment}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="segment-count">{s.count} ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* Generate a Report */}
        <section className="report-section">
          <h3>Generate Report</h3>
          <p>Pulls from published assessments and response data for the selected project.</p>
          {genMsg && <p className={genMsg.includes('failed') || genMsg.includes('Select') ? 'error-msg' : 'success-msg'}>{genMsg}</p>}
          <form className="gen-report-form" onSubmit={handleGenerate}>
            <input
              placeholder="Report Title"
              value={genForm.title}
              onChange={(e) => setGenForm({ ...genForm, title: e.target.value })}
              required
            />
            <select value={genForm.type} onChange={(e) => setGenForm({ ...genForm, type: e.target.value })}>
              <option value="public_summary">Public Summary</option>
              <option value="tenant_dashboard">Tenant Dashboard</option>
              <option value="member_report">Member Report</option>
              <option value="segment_export">Segment Export</option>
            </select>
            <textarea
              placeholder="Recommendations (one per line, optional)"
              value={genForm.recommendations}
              onChange={(e) => setGenForm({ ...genForm, recommendations: e.target.value })}
              rows={3}
            />
            <button type="submit" disabled={generating}>
              {generating ? 'Generating...' : 'Generate Report'}
            </button>
          </form>
        </section>

        {/* Generated Reports List */}
        <section className="report-section">
          <h3>Generated Reports</h3>
          {generatedReports.length === 0 ? (
            <p>No reports generated yet.</p>
          ) : (
            <table className="report-table">
              <thead>
                <tr><th>Title</th><th>Type</th><th>Status</th><th>Date</th><th></th></tr>
              </thead>
              <tbody>
                {generatedReports.map((r) => (
                  <tr key={r._id}>
                    <td>{r.title}</td>
                    <td>{r.type.replace(/_/g, ' ')}</td>
                    <td><span className={`status-badge status-${r.status}`}>{r.status}</span></td>
                    <td>{new Date(r.createdAt).toLocaleDateString()}</td>
                    <td><Link to={`/reports/${r._id}`}>View</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
  );
};

export default ReportsPage;
